import { ITravelAttachment } from '../types';

interface BlobQueryResult {
  unwrap: () => Promise<Blob>;
}

type BlobQueryTrigger<TArgument> = (argument: TArgument) => BlobQueryResult;

/**
 * Common function to preview an attachment (file or API blob)
 * @param attachment ITravelAttachment object
 * @param getAttachment RTK Query function to fetch file from API
 * @param setPreviewFile setState function for preview file URL
 * @param setPreviewName setState function for preview file name
 * @param travelRequestId (optional) travel request id for API fetch
 */
export async function previewAttachment<TTravelArgument, TGalleryArgument>(
  attachment: ITravelAttachment,
  getAttachment: BlobQueryTrigger<TTravelArgument>,
  setPreviewFile: (url: string | null) => void,
  setPreviewName: (name: string | null) => void,
  travelRequestId?: number | string,
  getGalleryAttachment?: BlobQueryTrigger<TGalleryArgument>,
): Promise<boolean> {
  try {
    let fileUrl: string;
    if (attachment.galleryFileId && getGalleryAttachment) {
      const blob = await getGalleryAttachment(
        attachment.galleryFileId as TGalleryArgument,
      ).unwrap();
      fileUrl = URL.createObjectURL(blob);
    } else if (attachment.id !== undefined && isNaN(+attachment.id)) {
      if (!attachment.file) throw new Error('No file available');
      fileUrl = URL.createObjectURL(attachment.file);
    } else {
      const blob = await getAttachment(
        {
          travelRequestId:
            travelRequestId || attachment.travelHeaderId || attachment.headerId,
          fileName: attachment.fileName,
        } as TTravelArgument,
      ).unwrap();
      fileUrl = URL.createObjectURL(blob);
    }
    setPreviewFile(fileUrl);
    setPreviewName(attachment.fileName || attachment.file?.name || '');
    return true;
  } catch (error) {
    console.error('Error fetching file:', error);
    return false;
  }
}

/**
 * Common function to download an attachment (file or API blob)
 * @param attachment ITravelAttachment object
 * @param getAttachment RTK Query function to fetch file from API
 * @param travelRequestId (optional) travel request id for API fetch
 */
export async function downloadAttachment<
  TTravelArgument,
  TGalleryArgument,
  TRequestArgument = never,
>(
  attachment: ITravelAttachment,
  getAttachment: BlobQueryTrigger<TTravelArgument>,
  travelRequestId?: number | string,
  getGalleryAttachment?: BlobQueryTrigger<TGalleryArgument>,
  getRequestAttachment?: BlobQueryTrigger<TRequestArgument>,
): Promise<boolean> {
  try {
    let blob: Blob;
    if (
      attachment.readSource &&
      attachment.readAttachmentId &&
      getRequestAttachment
    ) {
      blob = await getRequestAttachment(
        {
          travelRequestId,
          source: attachment.readSource,
          attachmentId: attachment.readAttachmentId,
        } as TRequestArgument,
      ).unwrap();
    } else if (attachment.galleryFileId && getGalleryAttachment) {
      blob = await getGalleryAttachment(
        attachment.galleryFileId as TGalleryArgument,
      ).unwrap();
    } else if (attachment.id !== undefined && isNaN(+attachment.id)) {
      if (!attachment.file) throw new Error('No file available');
      blob = attachment.file;
    } else {
      blob = await getAttachment(
        {
          travelRequestId:
            travelRequestId || attachment.travelHeaderId || attachment.headerId,
          fileName: attachment.fileName,
        } as TTravelArgument,
      ).unwrap();
    }
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = attachment.fileName || attachment.file?.name || '';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
    return true;
  } catch (error) {
    console.error('Error downloading file:', error);
    return false;
  }
}
